import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, ListItem, Table } from "@mantine/core";

import range from "lodash/range";
import { Fragment } from "react/jsx-runtime";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getContractionsRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface ContractionsRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isPreviousVersion?: boolean;
  isViewOnly?: boolean;
}

export default function ContractionsRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isPreviousVersion,
  isViewOnly = false,
}: ContractionsRowProps) {
  const { frequency, duration, strength, coping, notes, hasAnswers, hasNotes } = useMemo(
    () => getContractionsRowValues(observation),
    [observation],
  );

  const previousVersions = hasPreviousVersions(observation);

  // Determine if details row is needed
  const detailsRowNeeded =
    !showNumbersOnly && (hasAnswers || hasNotes || notes || isPreviousVersion);

  // Row span for TimeCell and ActionCell
  const rowSpan = hasAnswers && detailsRowNeeded && !showNumbersOnly ? 2 : 1;

  // Show contractions summary in details row only for previous versions
  const showDetailsSummary = isPreviousVersion;

  const showTitle = coping;

  return (
    <Fragment>
      {/* Value row: only if there are answers and not a previous version */}
      {hasAnswers && !isPreviousVersion && showPreviousVersions && (
        <Table.Tr bg="white">
          <TimeCell
            time={time}
            rowSpan={rowSpan}
            isViewOnly={isViewOnly}
            observation={observation}
          />
          {range(4).map((i) => (
            <Table.Td key={i} />
          ))}
          <Table.Td>{frequency}</Table.Td>
          <Table.Td>{duration}</Table.Td>
          <Table.Td>{strength}</Table.Td>
          {range(3).map((i) => (
            <Table.Td key={i} />
          ))}
          {!isViewOnly && <ActionCell rowSpan={rowSpan} observation={observation} />}
        </Table.Tr>
      )}
      {/* Details row: if there are notes/details or isPreviousVersion is true */}
      {detailsRowNeeded && (
        <Table.Tr bg="white" className={classes.tableRow}>
          {showPreviousVersions && (!hasAnswers || isPreviousVersion) && (
            <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
          )}
          <Table.Td p="md" colSpan={10}>
            {showTitle && (
              <TextWrapper pt="xs" size="sm">
                Contractions:
              </TextWrapper>
            )}
            <List fz="sm" withPadding>
              {coping && (
                <ListItem>
                  <strong>Coping:</strong> {coping}
                </ListItem>
              )}
              {showDetailsSummary && (
                <>
                  {frequency && (
                    <ListItem>
                      <strong>Frequency:</strong> {frequency}
                    </ListItem>
                  )}
                  {duration && (
                    <ListItem>
                      <strong>Duration:</strong> {duration}
                    </ListItem>
                  )}
                  {strength && (
                    <ListItem>
                      <strong>Strength:</strong> {strength}
                    </ListItem>
                  )}
                </>
              )}
            </List>
            {notes && (
              <TextWrapper pt="xs" size="sm">
                Contractions Comments: {notes}
              </TextWrapper>
            )}
            {showPreviousVersions && previousVersions ? (
              <PreviousVersions
                observation={observation}
                showPreviousVersions={showPreviousVersions}
                rowComponent={(props) => <ContractionsRow {...props} isPreviousVersion={true} />}
              />
            ) : (
              <LastUpdated observation={observation} showPreviousVersions={false} />
            )}
          </Table.Td>
          {/* Only render ActionCell in details row if value row is not present and not a previous version */}
          {(!hasAnswers || isPreviousVersion) && !isPreviousVersion && !isViewOnly && (
            <ActionCell rowSpan={1} observation={observation} />
          )}
        </Table.Tr>
      )}
    </Fragment>
  );
}
